import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Button from 'components/ReusableComponents/Button'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import InviteEmailDesignDeleteModal from 'features/InviteEmailDesign/InviteEmailDesignDeleteModal'
import InviteEmailDesignCreateModal from 'features/InviteEmailDesign/InviteEmailDesignCreateModal'
import InviteEmailDesignTemplateCard from 'features/InviteEmailDesign/InviteEmailDesignTemplateCard'
import InviteEmailDesignTemplatePreviewModal from 'features/InviteEmailDesign/InviteEmailDesignTemplatePreviewModal'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import { invalidateInviteEmailDesignPreviewCache } from 'features/InviteEmailDesign/useInviteEmailDesignPreview'
import { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'

import styles from './InviteEmailDesign.module.scss'

const InviteEmailDesign = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()

  const [templates, setTemplates] = useState([])
  const [needsInitialApply, setNeedsInitialApply] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [previewModalTemplate, setPreviewModalTemplate] = useState(null)
  const [deleteModalTemplate, setDeleteModalTemplate] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const activeTemplate = useMemo(
    () => templates.find(template => template.isActive),
    [templates]
  )

  function openPreviewModal(template) {
    setPreviewModalTemplate(template)
  }

  function loadTemplates() {
    setIsLoading(true)
    return inviteEmailDesignApi
      .listTemplates()
      .then(data => {
        invalidateInviteEmailDesignPreviewCache()
        setTemplates(data?.templates || [])
        setNeedsInitialApply(Boolean(data?.needsInitialApply))
      })
      .catch(() => {
        addToast('Failed to load email design templates.', { appearance: 'error' })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  function handleCreateTemplate(name) {
    setIsCreating(true)
    inviteEmailDesignApi
      .createTemplate({ name })
      .then(created => {
        setIsCreateModalOpen(false)
        if (created?.id) {
          history.push(`/invite-email-design/${created.id}`)
          return
        }

        addToast('Template created.', { appearance: 'success' })
        return loadTemplates()
      })
      .catch(() => {
        addToast('Failed to create email design template.', { appearance: 'error' })
      })
      .finally(() => {
        setIsCreating(false)
      })
  }

  function openDeleteModal(template) {
    if (template.isActive) {
      return
    }

    setDeleteModalTemplate(template)
  }

  function closeDeleteModal() {
    if (!isDeleting) {
      setDeleteModalTemplate(null)
    }
  }

  function confirmDelete() {
    if (!deleteModalTemplate) {
      return
    }

    setIsDeleting(true)
    inviteEmailDesignApi
      .deleteTemplate({ templateId: deleteModalTemplate.id })
      .then(() => {
        addToast('Template deleted.', { appearance: 'success' })
        setDeleteModalTemplate(null)
        return loadTemplates()
      })
      .catch(() => {
        addToast('Failed to delete template.', { appearance: 'error' })
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }

  function openCreateModal() {
    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    setIsCreateModalOpen(true)
  }

  return (
    <div className={styles.page}>
      <CustomHeader title="Email design" width={960} />
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Email design templates</h1>
          <p className={styles.pageIntro}>
            Create and manage shared invite email layouts — logo, hero, colors, and footer.
            Apply a design to campaigns from Manage Campaigns → Email Template Configuration.
          </p>
        </div>
        <div className={styles.pageHeaderActions}>
          <Button onClick={openCreateModal}>Create template</Button>
        </div>
      </div>

      {needsInitialApply && activeTemplate && (
        <div className={styles.notice}>
          Your default design is ready.&nbsp;
          <Link className={styles.noticeLink} to="/manageCampaigns/sales">
            Open Manage Campaigns → Email Template Configuration
          </Link>
          &nbsp;to apply it to invite campaigns.
        </div>
      )}

      {isLoading ? (
        <div className={styles.templateLoading}>
          <LoadingIndicator />
        </div>
      ) : (
        <div className={styles.templateGrid}>
          {templates.map(template => (
            <InviteEmailDesignTemplateCard
              key={template.id}
              template={template}
              onPreview={openPreviewModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      <InviteEmailDesignCreateModal
        isOpen={isCreateModalOpen}
        isSubmitting={isCreating}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateTemplate}
      />

      <InviteEmailDesignTemplatePreviewModal
        template={previewModalTemplate}
        onClose={() => setPreviewModalTemplate(null)}
      />

      <InviteEmailDesignDeleteModal
        isOpen={Boolean(deleteModalTemplate)}
        template={deleteModalTemplate}
        isSubmitting={isDeleting}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

export default InviteEmailDesign
